import React, {useCallback, useEffect, useRef} from 'react';
import Radio from '../../../GbsUI/UI/Radio';
import TextInput from '../../../GbsUI/UI/TextInput';

const US_AUTH_OPTIONS = [
  {
    label: 'Yes, Permanently.',
    help: 'I have permanent authorization (e.g., US citizen, permanent resident, or similar)',
    value: 'permanent',
    bold: true,
  },
  {
    label: 'Yes, Presently.',
    help: 'I have current work authorization for a defined time period (e.g., Visa, including temporary visas)',
    value: 'present',
    bold: true,
  },
  {
    label: 'Expected / In-Process.',
    help: 'I do not have current US work authorization but have applied for resident status, visas or other work authorization or my application is in process.',
    value: 'expected',
    bold: true,
  },
  {
    label: 'No / Unclear.',
    help: 'I need Visa sponsorship now or in the near future or am unclear about my current status.',
    value: 'no',
    bold: true,
  },
  {label: 'Other (please explain).', value: 'other', bold: true},
];

const GENDER_OPTIONS = [
  {label: 'Woman', value: 'woman'},
  {label: 'Man', value: 'man'},
  {label: 'Non-binary / Other', value: 'other'},
];

const RACE_OPTIONS = [
  {label: 'Black or African American', value: 'black'},
  {label: 'Hispanic or Latino', value: 'latino'},
  {label: 'Asian (incl. East Asian, Southeast Asian, and South Asian/Indian)', value: 'asian'},
  {label: 'Native American or Alaska Native', value: 'american'},
  {label: 'Native Hawaiian or Other Pacific Islander', value: 'hawaiian'},
  {label: 'Middle Eastern or North African', value: 'arfican'},
  {label: 'White or Caucasian', value: 'white'},
  {label: 'Other', value: 'other'},
];

const LGBTQ_OPTIONS = [
  {label: 'Yes - I identify as LGBTQ+', value: 'yes'},
  {label: 'No - I do not', value: 'no'},
];

const MILITARY_OPTIONS = [
  {label: 'Yes - I have served or am serving in the US military', value: 'us_military'},
  {label: 'Yes - I have served or am serving in another country\'s military', value: 'military'},
  {label: 'No - I have not served in the military', value: 'no_military'},
];

const DISCLOSE_OPTIONS = [
  {label: 'Prefer not to disclose', value: 'prefer_not_to_say'},
  {
    label: 'OK to disclose to employers and for DEI initiatives',
    value: 'ok_to_say'
  },
];

const SPORTS_OPTIONS = [
  {label: 'No, intramurals or not disclosed', value: 'No'},
  {label: 'Yes - Club', value: 'Club'},
  {label: 'Yes - Varsity', value: 'Varsity'},
];

const QuestionsPage = ({state, updateState, history}) => {
  const formRef = useRef(null);

  const onSubmit = useCallback((e) => {
    e.preventDefault();

    history.push('/student/interests');
  }, [state]);

  useEffect(() => {
    updateState({
      canContinue: true,
      onContinue: () => formRef.current.requestSubmit(),
    });
  }, []);

  useEffect(() => {
    if (!state.loading && !state.signUpFilled) {
      history.replace('/student/signup');
    }
  }, [state]);

  return (
    <>
      <h1 className={'student-signup__title student-signup__title--small student-signup__title--left'}>Work Status and Demographics</h1>
      <p className={'student-signup__description student-signup__description--left'}>Please share your US work eligibility</p>
      <div className={'student-signup__content-wrap'}>
        <form ref={formRef} onSubmit={onSubmit} className={'student-form'}>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                options={US_AUTH_OPTIONS}
                label={'Are you authorized for full-time employment in the US?'}
                name={'usAuth'}
                checked={state.data.usAuth}
                onChange={(e) => updateState({
                  data: {
                    usAuth: e.target.value,
                    usAuthExplain: '',
                  }
                })}
                showHelpTooltip={false}
                showCustom={true}
                customRadioValue={['other']}
                customPlaceholder={'Please explain'}
                customName={'usAuthExplain'}
                customOnChange={(e) => updateState({data: {usAuthExplain: e.target.value}})}
                customValue={state.data.usAuthExplain}
                customRequired
                required
              />
            </div>
          </div>
          <div className={'student-signup__subheader'}>
            <p className={'student-signup__description student-signup__description--left student-signup__description--dark'}>Please share demographic
              info (optional)</p>
            <p className={'student-signup__description student-signup__description--left student-signup__description--small'}>As part of our commitment to equal employment opportunity, GoBuyside collects optional demographic information to support the inclusion
              of underrepresented groups in the finance community. These questions are optional. In all cases, GoBuyside is dedicated to promoting equal
              opportunity in hiring for all candidates, regardless of race, color, national origin, sex, gender identity, sexual orientation, or
              creed.</p>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                name={'discloseGender'}
                options={DISCLOSE_OPTIONS}
                label={'Which of these best describes your gender?'}
                checked={state.data.discloseGender}
                onChange={(e) => updateState({
                  data: {
                    discloseGender: e.target.value,
                    gender: e.target.value === 'ok_to_say' ? state.data.gender : '',
                  }
                })}
              />
              {state.data.discloseGender === 'ok_to_say' && (
                <div className={'student-sub-input'}>
                  <Radio
                    name={'gender'}
                    options={GENDER_OPTIONS}
                    checked={state.data.gender}
                    onChange={(e) => updateState({data: {gender: e.target.value}})}
                    required
                  />
                </div>
              )}
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                label={'Which of the following best describes your racial identity?'}
                name={'discloseRace'}
                options={DISCLOSE_OPTIONS}
                checked={state.data.discloseRace}
                onChange={(e) => updateState({
                  data: {
                    discloseRace: e.target.value,
                    race: e.target.value === 'ok_to_say' ? state.data.race : [],
                    raceCustom: e.target.value === 'ok_to_say' ? state.data.raceCustom : '',
                  }
                })}
                required
              />
              {state.data.discloseRace === 'ok_to_say' && (
                <div className={'student-sub-input'}>
                  <Radio
                    name={'race'}
                    options={RACE_OPTIONS}
                    checked={state.data.race}
                    onChange={(e) => {
                      let race = [...state.data.race];

                      if (!Array.isArray(race)) {
                        race = [];
                      }

                      if (e.target.checked && race.indexOf(e.target.value) === -1) {
                        race.push(e.target.value);
                      } else if (!e.target.checked && race.indexOf(e.target.value) !== -1) {
                        race = race.filter(r => r !== e.target.value);
                      }

                      updateState({
                        data: {
                          race,
                          raceCustom: race.indexOf('other') !== -1 ? state.data.raceCustom : '',
                        }
                      });
                    }}
                    showCustom={true}
                    customRadioValue={'other'}
                    customPlaceholder={'Specify if desired'}
                    customName={'raceCustom'}
                    customOnChange={(e) => updateState({data: {raceCustom: e.target.value}})}
                    customValue={state.data.raceCustom}
                    required
                    multiple
                    checkedBold
                  />
                </div>
              )}
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                label={'Do you identify as LGBTQ+?'}
                name={'discloseLgbtq'}
                options={DISCLOSE_OPTIONS}
                checked={state.data.discloseLgbtq}
                onChange={(e) => updateState({
                  data: {
                    discloseLgbtq: e.target.value,
                    lgbtq: e.target.value === 'ok_to_say' ? state.data.lgbtq : '',
                  }
                })}
                required
              />
              {state.data.discloseLgbtq === 'ok_to_say' && (
                <div className={'student-sub-input'}>
                  <Radio
                    name={'lgbtq'}
                    options={LGBTQ_OPTIONS}
                    checked={state.data.lgbtq}
                    onChange={(e) => updateState({data: {lgbtq: e.target.value}})}
                    required
                  />
                </div>
              )}
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                label={'Have you served or are you currently serving in the military?'}
                name={'discloseMilitary'}
                options={DISCLOSE_OPTIONS}
                checked={state.data.discloseMilitary}
                onChange={(e) => updateState({
                  data: {
                    discloseMilitary: e.target.value,
                    military: e.target.value === 'ok_to_say' ? state.data.military : '',
                    militaryNotes: e.target.value === 'ok_to_say' ? state.data.militaryNotes : '',
                  }
                })}
                required
              />
              {state.data.discloseMilitary === 'ok_to_say' && (
                <div className={'student-sub-input'}>
                  <Radio
                    name={'military'}
                    options={MILITARY_OPTIONS}
                    checked={state.data.military}
                    onChange={(e) => updateState({
                      data: {
                        military: e.target.value,
                        militaryNotes: e.target.value === 'us_military' || e.target.value === 'military' ? state.data.militaryNotes : '',
                      }
                    })}
                    required
                  />
                  {(state.data.military === 'us_military' || state.data.military === 'military') && (
                    <TextInput
                      label={'Military Branch / Notes'}
                      name={'militaryNotes'}
                      value={state.data.militaryNotes}
                      onChange={(e) => updateState({data: {militaryNotes: e.target.value}})}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <Radio
                label={'Did you participate in varsity sports during college?'}
                name={'athlete'}
                options={SPORTS_OPTIONS}
                checked={state.data.athlete}
                onChange={(e) => updateState({
                  data: {
                    athlete: e.target.value,
                  }
                })}
                required
              />
              {(state.data.athlete === 'Club' || state.data.athlete === 'Varsity') && (
                <TextInput
                  label={'Please list sports played / comments'}
                  name={'sports'}
                  value={state.data.sports}
                  onChange={(e) => updateState({data: {sports: e.target.value}})}
                  required={state.data.athlete === 'Club' || state.data.athlete === 'Varsity'}
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default QuestionsPage;
